/**
 * The Kaizen design system's MUI theme.
 *
 * Every value comes from `tokens/generated`, built from design's Figma export —
 * see `design-tokens/README.md`. MUI emits these as custom properties, so its
 * own color maths runs in CSS rather than needing the values in JavaScript.
 *
 * That means derived slots — `contrastText`, and `light`/`dark` where we don't
 * supply them — resolve to `oklch()` and `color-mix()` expressions rather than
 * hex. Valid CSS, but not parseable by MUI's JS `alpha()`/`darken()` helpers,
 * so read those values in stylesheets rather than in JavaScript.
 *
 * Browser support: Chrome 119+, Safari 16.4+, Firefox 128+.
 *
 * Usage:
 *   import { kaizenTheme } from '@platform/design-system';
 *
 *   <ThemeProvider theme={kaizenTheme}> ... </ThemeProvider>
 */
import { tokens } from "../tokens/generated/tokens";

import { CSS_VAR_PREFIX } from "./palette-var";
import { createSharedTheme, palette as sharedPalette } from "./shared";

import type {
  PaletteOptions,
  ThemeOptions,
  TypographyVariantsOptions,
} from "@mui/material/styles";

const { light, dark } = tokens.theme;

/**
 * Descendant selector matching the scheme MUI stamps on <html> when this
 * theme's cssVariables are active, so a rule under it applies in dark only.
 */
const DARK_SCHEME = '[data-mui-color-scheme="dark"] &';

/** A palette token as the custom property this theme emits for it. */
const schemeVar = (token: string): string =>
  `var(--${CSS_VAR_PREFIX}-palette-${token})`;

// Dark surfaces sit on the same paper as the ground: there is no separate
// "raised" token, so cards and menus lean on borders for separation, and
// hover is a translucent overlay since a raised surface would equal the menu
// background and show no hover at all.
const darkScheme = {
  surface: schemeVar("background-paper"),
  hover: schemeVar("action-hover"),
  border: schemeVar("divider"),
  text: schemeVar("text-primary"),
  textDim: schemeVar("text-secondary"),
  accent: schemeVar("primary-main"),
};
const text = tokens.typography.typography.text;
const { weight } = tokens.base.typography.text;

/** Design authors sizes in px; rem keeps them tracking the browser font size. */
const rem = (px: number): string => `${px / 16}rem`;

// Design names one family. The fallbacks are ours, for before Montserrat loads.
const FONT_FAMILY = `${tokens.base.typography.fontStack.sansSerif}, Avenir, Corbel, "URW Gothic", "Helvetica Neue", Arial, sans-serif`;

/** Maps one mode's tokens onto MUI's palette slots. */
// What MUI derives for a palette color's `dark` and `contrastText` under
// `nativeColor`. Custom palette colors don't get derived slots, so the brand
// colors below spell them out to match the roles they mirror. The formulas
// copy undocumented MUI internals (`--__l`, `--__a` from @mui/system's
// prepareCssVars); Broker Portal's `theme/brand-colours.test.ts` fails if a MUI upgrade changes them,
// which means MUI changed its derivation, not that the brand colors broke.
const nativeDark = (main: string): string =>
  `color-mix(in oklch, ${main}, #000 30%)`;
const nativeContrastText = (main: string): string =>
  `oklch(from ${main} var(--__l) 0 h / var(--__a))`;

const paletteFor = (mode: typeof light): PaletteOptions => ({
  // The design system's own vocabulary — base components read this rather than
  // MUI's `primary`/`secondary`, which the legacy codebase has inverted. See
  // the `brand` note in shared.tsx.
  brand: {
    primary: mode.fgColor.primary,
    // Hover darkens the purple in light mode and lightens it in dark mode, so
    // read design's per-mode button hover rather than a fixed dark purple.
    primaryHover: mode.button.primary.bgColor.hover,
    // Readable label on the filled brand button; flips white/near-black with the
    // scheme so contrast holds on both the dark- and light-purple button.
    onPrimary: mode.fgColor.onEmphasis,
    supporting: mode.fgColor.secondary,
  },
  // MUI reads `light` and `dark` for derived states — contained-button hover
  // comes from `dark`. Design has no token for those slots, so these map onto
  // the nearest values they do define: `bgColor.primaryMuted`, and the purple
  // their own `button.primary.bgColor.hover` resolves to. Without them the app's
  // teal shades survive underneath this purple `main` and buttons hover teal.
  primary: {
    main: mode.fgColor.primary,
    light: mode.bgColor.primaryMuted,
    dark: mode.button.primary.bgColor.hover,
  },
  secondary: {
    main: mode.fgColor.secondary,
    light: mode.bgColor.secondaryMuted,
  },
  // The brand colors under fixed names (see `Palette.brandPrimary`): here the
  // same values as `primary` and `secondary`. Every slot is set, so none of the
  // app theme's legacy teal survives the merge underneath.
  brandPrimary: {
    main: mode.fgColor.primary,
    light: mode.bgColor.primaryMuted,
    dark: mode.button.primary.bgColor.hover,
    contrastText: nativeContrastText(mode.fgColor.primary),
  },
  brandSupporting: {
    main: mode.fgColor.secondary,
    light: mode.bgColor.secondaryMuted,
    dark: nativeDark(mode.fgColor.secondary),
    medium: mode.fgColor.secondary,
    contrastText: nativeContrastText(mode.fgColor.secondary),
  },
  error: { main: mode.fgColor.danger },
  warning: { main: mode.fgColor.attention },
  info: { main: mode.fgColor.accent },
  success: { main: mode.fgColor.success },
  text: {
    primary: mode.fgColor.default,
    secondary: mode.fgColor.muted,
    disabled: mode.fgColor.disabled,
  },
  divider: mode.borderColor.default,
  background: {
    default: mode.bgColor.default,
    paper: mode.bgColor.default,
  },
  action: {
    disabled: mode.fgColor.disabled,
    disabledBackground: mode.bgColor.disabled,
  },
});

/**
 * `lineHeight` arrives as a unitless ratio and is passed through; `letterSpacing`
 * arrives in px and is converted to `em`, so it tracks the font size.
 */
const variant = (
  fontSize: number,
  lineHeight: number,
  fontWeight: number,
  letterSpacing: number,
): TypographyVariantsOptions["h1"] => ({
  fontSize: rem(fontSize),
  lineHeight,
  fontWeight,
  letterSpacing: `${letterSpacing / fontSize}em`,
});

// Exported as options as well as a theme: the app composes these into its own
// theme options and calls `createTheme` once on the result, so MUI derives
// light/dark/contrastText from the merged palette rather than from each half.
export const kaizenThemeOptions: ThemeOptions = {
  cssVariables: {
    nativeColor: true,
    cssVarPrefix: CSS_VAR_PREFIX,
    // Dark applies through a data attribute the toggle sets, not `@media
    // (prefers-color-scheme)`, so it's an explicit choice rather than a side
    // effect of the reader's OS.
    colorSchemeSelector: "data-mui-color-scheme",
  },
  // The top-level `palette` is the light scheme (MUI treats it as `colorSchemes.light`
  // when `colorSchemes` omits `light`); keeping it top-level is also what the app's
  // `deepmerge` composition overrides onto its own palette. `dark` is opt-in via the
  // toggle, defaulting to light.
  palette: paletteFor(light),
  colorSchemes: {
    dark: {
      palette: {
        ...paletteFor(dark),
        // The base `ai` palette (shared.tsx) is tuned for light surfaces: pale
        // review/validated cell fills and a near-black infoLabel that vanish or
        // go unreadable on the dark ground. Deepen the fills and lift the
        // foregrounds so each keeps its meaning under light text. Spreads the
        // base `ai` to retain its `chips` and any other keys. Unlike the core
        // dark tokens, the `ai` namespace isn't in design's export, so these
        // values are our proposal for design to ratify.
        ai: {
          ...sharedPalette.ai,
          infoLabel: "#c4c4c4",
          extracted: "#64b5f6",
          comment: "#ffb74d",
          reviewBackground: "#3e3a1c",
          validatedBackground: "#1e3a28",
          extractionError: "#ffb74d",
          edited: "#bdbdbd",
        },
      },
    },
  },
  defaultColorScheme: "light",
  typography: {
    // Keys here are MUI's variant names; the `Typography` component maps the
    // Centro names onto them. Design defines five heading styles to MUI's six,
    // so `h6` repeats `title.xsmall` — the only slot without its own value.
    // Renaming the Centro variants to match design's vocabulary is CD-2041.
    fontFamily: FONT_FAMILY,
    h1: variant(
      text.display.fontSize,
      text.display.lineHeight,
      text.display.fontWeight,
      text.display.letterSpacing,
    ),
    h2: variant(
      text.title.fontSize.large,
      text.title.lineHeight.large,
      text.title.fontWeight.large,
      text.title.letterSpacing.large,
    ),
    h3: variant(
      text.title.fontSize.medium,
      text.title.lineHeight.medium,
      text.title.fontWeight.medium,
      text.title.letterSpacing.medium,
    ),
    h4: variant(
      text.title.fontSize.small,
      text.title.lineHeight.small,
      text.title.fontWeight.small,
      text.title.letterSpacing.small,
    ),
    h5: variant(
      text.title.fontSize.xsmall,
      text.title.lineHeight.xsmall,
      text.title.fontWeight.xsmall,
      text.title.letterSpacing.xsmall,
    ),
    h6: variant(
      text.title.fontSize.xsmall,
      text.title.lineHeight.xsmall,
      text.title.fontWeight.xsmall,
      text.title.letterSpacing.xsmall,
    ),
    subtitle1: variant(
      text.subtitle.fontSize.medium,
      text.subtitle.lineHeight.medium,
      text.subtitle.fontWeight.medium,
      text.subtitle.letterSpacing.medium,
    ),
    subtitle2: variant(
      text.subtitle.fontSize.small,
      text.subtitle.lineHeight.small,
      text.subtitle.fontWeight.small,
      text.subtitle.letterSpacing.small,
    ),
    body1: variant(
      text.body.fontSize.large,
      text.body.lineHeight.large,
      text.body.fontWeight,
      text.body.letterSpacing.large,
    ),
    body2: variant(
      text.body.fontSize.medium,
      text.body.lineHeight.medium,
      text.body.fontWeight,
      text.body.letterSpacing.medium,
    ),
    caption: variant(
      text.caption.fontSize,
      text.caption.lineHeight,
      text.caption.fontWeight,
      text.caption.letterSpacing,
    ),
    overline: variant(
      text.overline.fontSize,
      text.overline.lineHeight,
      text.overline.fontWeight,
      text.overline.letterSpacing,
    ),
    button: {
      textTransform: "none" as const,
      fontWeight: weight.medium,
    },
  },
  shape: {
    borderRadius: tokens.functional.size.borderRadius.default,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // CssBaseline already paints `palette.background.default`, which now
        // carries the right value per mode, so only the family is set here.
        "body, html, #root": {
          fontFamily: FONT_FAMILY,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: "contained",
      },
      styleOverrides: {
        root: {
          fontFamily: "inherit",
        },
      },
    },
    // The shared theme's overrides bake light-mode colors into inputs, menus
    // and cards. Each block below flips just those under the dark scheme; the
    // values are all token vars, so a dark-token change lands here unedited.
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          [DARK_SCHEME]: {
            // Background comes from the scheme-aware MuiInputBase override;
            // this only corrects the outline and text, which MUI leaves light.
            ".MuiOutlinedInput-notchedOutline": {
              borderColor: darkScheme.border,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: darkScheme.textDim,
            },
            input: {
              color: darkScheme.text,
              "&::placeholder": {
                color: darkScheme.textDim,
                opacity: 1,
              },
            },
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          [DARK_SCHEME]: {
            backgroundColor: darkScheme.surface,
            // backgroundImage:none removes MUI's elevation tint.
            backgroundImage: "none",
            border: `1px solid ${darkScheme.border}`,
            color: darkScheme.text,
            // Some menu items wrap their label in a Typography that bakes the
            // light text.primary literal; force it too, not just the item.
            "& .MuiMenuItem-root, & a, & .MuiTypography-root": {
              color: darkScheme.text,
            },
            "& .MuiListItemIcon-root, & .MuiSvgIcon-root": {
              color: darkScheme.textDim,
            },
            "& .MuiMenuItem-root:hover": {
              backgroundColor: darkScheme.hover,
            },
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          [DARK_SCHEME]: {
            // Menu items read text.primary as a fixed light-mode literal, so
            // force the dark text token onto the item and any link it renders as.
            "&, & a": {
              color: darkScheme.text,
            },
            "&:hover": {
              backgroundColor: darkScheme.hover,
            },
            "&.Mui-selected": {
              color: darkScheme.accent,
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          [DARK_SCHEME]: {
            // The card background already flips via background.paper; shadows
            // barely register on a dark ground, so add a hairline border and a
            // deep shadow for depth.
            border: `1px solid ${darkScheme.border}`,
            boxShadow:
              "0 1px 2px rgba(0,0,0,0.45), 0 12px 28px rgba(0,0,0,0.35)",
          },
        },
      },
    },
  },
};

/**
 * The Kaizen options layered over the shared component rules, the same
 * composition every app builds through `createSharedTheme`, so an app that
 * mounts this export gets the navigation, menu and list styling too.
 */
export const kaizenTheme = createSharedTheme(kaizenThemeOptions);
